import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getProject } from '../api/projects'
import { getProjectNetworks } from '../api/commNetworks'
import { getProjectInterfaces } from '../api/projectInterfaces'
import { getNetworkWifiClients } from '../api/wifiClients'
import ReactMarkdown from 'react-markdown'
import remarkBreaks from 'remark-breaks'
import remarkGfm from 'remark-gfm'
import { Printer, ArrowLeft } from 'lucide-react'

// Функция для автовычисления полного IP
function calculateFullIp(subnet: string | undefined, lastOctet: number | string | null): string {
  if (!subnet) return ''
  if (lastOctet === null || lastOctet === undefined || lastOctet === '') return ''
  
  const cleanSubnet = subnet.split('/')[0].trim()
  const parts = cleanSubnet.split('.')
  if (parts.length >= 3) {
    return `${parts[0]}.${parts[1]}.${parts[2]}.${lastOctet}`
  }
  return ''
}

const getCombinedDeviceText = (item: any): string => {
  const parts = []
  if (item.manufacturer) parts.push(item.manufacturer)
  if (item.specification_name) parts.push(item.specification_name)
  const details = parts.length > 0 ? ` (${parts.join(' ')})` : ''
  return `${item.device_name || ''}${details}`
}

const getWifiCombinedDeviceText = (client: any): string => {
  const parts = []
  if (client.manufacturer) parts.push(client.manufacturer)
  if (client.specification_name) parts.push(client.specification_name)
  const details = parts.length > 0 ? ` (${parts.join(' ')})` : ''
  return `${client.device_name || ''}${details}`
}

interface HostRow {
  id: string;
  isWifi: boolean;
  deviceLabel: string;
  deviceName: string;
  portLabel: string;
  addressVal: number | null;
  fullIp: string;
  macAddress: string;
  note: string;
  deviceInstanceId?: number | null;
}

type SortField = 'deviceLabel' | 'deviceName' | 'portLabel' | 'addressVal' | 'macAddress' | 'note';
type SortDirection = 'asc' | 'desc';

export default function ProjectNetworkPrintPage() {
  const { projectId, networkId } = useParams<{ projectId: string, networkId: string }>()
  const navigate = useNavigate()

  const [sortField, setSortField] = useState<SortField>('addressVal')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')

  // Загрузка проекта
  const { data: project, isLoading: projectLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => getProject(Number(projectId)),
    enabled: !!projectId,
  })

  // Загрузка сетей проекта
  const { data: networks = [], isLoading: networksLoading } = useQuery({
    queryKey: ['projectNetworks', projectId],
    queryFn: () => getProjectNetworks(Number(projectId!)),
    enabled: !!projectId
  })

  // Загрузка интерфейсов проекта
  const { data: interfaces = [], isLoading: interfacesLoading } = useQuery({
    queryKey: ['projectInterfaces', projectId],
    queryFn: () => getProjectInterfaces(Number(projectId!)),
    enabled: !!projectId
  })

  // Определение текущей выбранной сети
  const currentNetwork = networks.find(n => n.id === Number(networkId))
  const isLan = currentNetwork?.network_type === 'lan'
  const isModbus = currentNetwork ? ['rs485', 'rs485_modbus'].includes(currentNetwork.network_type) : false

  // Загрузка WiFi-клиентов
  const { data: wifiClients = [], isLoading: wifiClientsLoading } = useQuery({
    queryKey: ['networkWifiClients', networkId],
    queryFn: () => getNetworkWifiClients(Number(networkId!)),
    enabled: !!networkId && isLan
  })

  const isLoading = projectLoading || networksLoading || interfacesLoading || (isLan && wifiClientsLoading)

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 text-gray-500">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-4"></div>
        <p className="font-medium text-sm">Загрузка данных для печати...</p>
      </div>
    )
  }

  if (!project || !currentNetwork) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 text-red-500">
        <p className="font-semibold text-lg">Проект или сеть не найдены</p>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-semibold transition-colors"
        >
          Назад
        </button>
      </div>
    )
  }

  // Фильтруем физические интерфейсы, привязанные к этой сети
  const boundInterfaces = interfaces.filter(item => item.network_id === currentNetwork.id)

  // Преобразуем физические интерфейсы в строки таблицы
  const physicalRows: HostRow[] = boundInterfaces.map(item => ({
    id: `phys_${item.device_instance_id}_${item.interface_id}`,
    isWifi: false,
    deviceLabel: item.device_position_label || '',
    deviceName: getCombinedDeviceText(item),
    portLabel: item.interface_chassis_label || '',
    addressVal: isLan ? item.ip_last_octet : (isModbus ? item.slave_id : null),
    fullIp: isLan && item.ip_last_octet !== null 
      ? calculateFullIp(currentNetwork.network_config?.subnet, item.ip_last_octet)
      : '',
    macAddress: item.mac_address || '',
    note: item.note || '',
    deviceInstanceId: item.device_instance_id
  }))

  // Преобразуем беспроводных клиентов в строки таблицы
  const wifiRows: HostRow[] = wifiClients.map(client => {
    const devName = client.device_instance_id 
      ? getWifiCombinedDeviceText(client)
      : '';
    const name = client.client_name + (devName ? ` (${devName})` : '');
    
    return {
      id: `wifi_${client.id}`,
      isWifi: true,
      deviceLabel: client.device_position_label || '',
      deviceName: name,
      portLabel: 'Wi-Fi 📶',
      addressVal: client.ip_last_octet,
      fullIp: client.ip_last_octet !== null
        ? calculateFullIp(currentNetwork.network_config?.subnet, client.ip_last_octet)
        : '',
      macAddress: client.mac_address || '',
      note: client.note || '',
      deviceInstanceId: client.device_instance_id
    }
  })

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const renderHeader = (label: string, field: SortField, extraClass: string = '') => {
    const isActive = sortField === field
    return (
      <th
        onClick={() => handleSort(field)}
        className={`px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-200 cursor-pointer select-none hover:text-slate-800 transition-colors group ${extraClass}`}
      >
        <div className="flex items-center gap-1">
          <span>{label}</span>
          <span className="no-print inline-flex items-center text-gray-400 group-hover:text-gray-600 transition-colors">
            {isActive ? (
              sortDirection === 'asc' ? ' ▲' : ' ▼'
            ) : (
              <span className="opacity-0 group-hover:opacity-100 text-[9px] font-normal transition-opacity"> ↕</span>
            )}
          </span>
        </div>
      </th>
    )
  }

  // Объединяем и сортируем динамически
  const combinedRows = [...physicalRows, ...wifiRows].sort((a, b) => {
    if (sortField === 'addressVal') {
      const valA = a.addressVal
      const valB = b.addressVal
      
      if (valA === null && valB === null) {
        const comp = a.deviceLabel.localeCompare(b.deviceLabel, 'ru', { numeric: true })
        return sortDirection === 'asc' ? comp : -comp
      }
      if (valA === null) return 1
      if (valB === null) return -1
      
      const comp = valA - valB
      return sortDirection === 'asc' ? comp : -comp
    }

    const valA = (a[sortField] || '').toString()
    const valB = (b[sortField] || '').toString()

    if (valA === '' && valB === '') {
      const comp = a.deviceLabel.localeCompare(b.deviceLabel, 'ru', { numeric: true })
      return sortDirection === 'asc' ? comp : -comp
    }
    if (valA === '') return 1
    if (valB === '') return -1

    const comp = valA.localeCompare(valB, 'ru', { numeric: true, sensitivity: 'base' })
    return sortDirection === 'asc' ? comp : -comp
  })

  // Подсчет дубликатов IP-адресов / Slave ID
  const addressOwners = new Map<number, Set<string>>()
  combinedRows.forEach(row => {
    if (row.addressVal !== null) {
      const ownerKey = row.deviceInstanceId
        ? `dev_${row.deviceInstanceId}`
        : row.id
      
      if (!addressOwners.has(row.addressVal)) {
        addressOwners.set(row.addressVal, new Set())
      }
      addressOwners.get(row.addressVal)!.add(ownerKey)
    }
  })

  const subnetConfig = currentNetwork.network_config || {}

  return (
    <div className="min-h-screen bg-gray-50 print:bg-white text-gray-900 print:text-black">
      {/* Стили для печати */}
      <style>{`
        @media print {
          body {
            background-color: white !important;
            color: black !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .no-print {
            display: none !important;
          }
          .print-container {
            padding: 0 !important;
            margin: 0 !important;
            max-width: 100% !important;
            box-shadow: none !important;
            background: transparent !important;
          }
          .print-table th {
            background-color: #f1f5f9 !important;
            color: black !important;
            border-bottom: 2px solid #cbd5e1 !important;
          }
          .print-table tr:nth-child(even) {
            background-color: #f8fafc !important;
          }
          .print-card {
            border: 1px solid #e2e8f0 !important;
          }
        }
      `}</style>

      {/* Панель управления (скрывается при печати) */}
      <div className="no-print bg-white border-b border-gray-200 py-3 px-6 sticky top-0 z-50 shadow-sm flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-blue-600 font-medium transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Назад к интерфейсам
        </button>
        
        <div className="flex items-center gap-4">
          <span className="text-xs text-gray-500 hidden sm:inline">
            Совет: Настройте книжную или альбомную ориентацию при печати в зависимости от ширины таблицы.
          </span>
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow-sm transition-colors"
          >
            <Printer className="w-4 h-4" />
            Печать
          </button>
        </div>
      </div>

      {/* Печатная область */}
      <div className="max-w-5xl mx-auto p-8 print-container my-6 print:my-0 bg-white shadow-md print:shadow-none rounded-xl border border-gray-200 print:border-none">
        
        {/* Шапка печатного документа */}
        <div className="border-b-2 border-gray-200 pb-6 mb-6">
          <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
            <div>
              <span className="text-xs text-gray-500 uppercase tracking-wider font-semibold font-mono">
                Проект: {project.name}
              </span>
              <h1 className="text-3xl font-extrabold text-slate-900 mt-1">
                Карта сетевых адресов сети «{currentNetwork.name}»
              </h1>
              <p className="text-sm text-slate-500 mt-1 font-mono">
                Код сети: {currentNetwork.code} | Тип: {currentNetwork.network_type}
              </p>
            </div>
            {currentNetwork.description && (
              <div className="max-w-md text-right md:text-right text-xs text-gray-500 font-sans italic bg-gray-50 print:bg-transparent p-3 rounded-lg border border-gray-100 print:border-none print:p-0">
                {currentNetwork.description}
              </div>
            )}
          </div>

          {/* Параметры конфигурации сети */}
          {Object.keys(subnetConfig).length > 0 && (
            <div className="mt-6 bg-slate-50 print:bg-slate-50/50 print-card border border-slate-200/60 rounded-xl p-5">
              <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-3">
                Параметры спецификации сети
              </h3>
              
              {isLan && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
                  {subnetConfig.subnet && (
                    <div>
                      <span className="block text-[10px] uppercase text-gray-400 font-bold">Подсеть:</span>
                      <span className="font-mono text-gray-800 font-bold">{subnetConfig.subnet}</span>
                    </div>
                  )}
                  {subnetConfig.gateway && (
                    <div>
                      <span className="block text-[10px] uppercase text-gray-400 font-bold">Шлюз:</span>
                      <span className="font-mono text-gray-800 font-bold">{subnetConfig.gateway}</span>
                    </div>
                  )}
                  {subnetConfig.vlan && (
                    <div>
                      <span className="block text-[10px] uppercase text-gray-400 font-bold">VLAN ID:</span>
                      <span className="font-mono text-gray-800 font-bold">{subnetConfig.vlan}</span>
                    </div>
                  )}
                  {subnetConfig.dns && (
                    <div>
                      <span className="block text-[10px] uppercase text-gray-400 font-bold">DNS:</span>
                      <span className="font-mono text-gray-800 font-bold">{subnetConfig.dns}</span>
                    </div>
                  )}
                  {subnetConfig.dhcp_range && (
                    <div className="col-span-2">
                      <span className="block text-[10px] uppercase text-gray-400 font-bold">Диапазон DHCP:</span>
                      <span className="font-mono text-gray-800 font-bold">{subnetConfig.dhcp_range}</span>
                    </div>
                  )}
                </div>
              )}

              {isModbus && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
                  {subnetConfig.baud_rate && (
                    <div>
                      <span className="block text-[10px] uppercase text-gray-400 font-bold">Baud Rate:</span>
                      <span className="font-mono text-gray-800 font-bold">{subnetConfig.baud_rate} bps</span>
                    </div>
                  )}
                  {subnetConfig.parity && (
                    <div>
                      <span className="block text-[10px] uppercase text-gray-400 font-bold">Parity:</span>
                      <span className="font-mono text-gray-800 font-bold uppercase">{subnetConfig.parity}</span>
                    </div>
                  )}
                  {(subnetConfig.data_bits || subnetConfig.stop_bits) && (
                    <div>
                      <span className="block text-[10px] uppercase text-gray-400 font-bold">Data/Stop Bits:</span>
                      <span className="font-mono text-gray-800 font-bold">
                        {subnetConfig.data_bits || '8'}/{subnetConfig.stop_bits || '1'}
                      </span>
                    </div>
                  )}
                  {subnetConfig.protocol && (
                    <div>
                      <span className="block text-[10px] uppercase text-gray-400 font-bold">Protocol:</span>
                      <span className="font-mono text-gray-800 font-bold uppercase">
                        {subnetConfig.protocol === 'modbus_rtu' ? 'Modbus RTU' : subnetConfig.protocol === 'modbus_ascii' ? 'Modbus ASCII' : subnetConfig.protocol}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {!isLan && !isModbus && currentNetwork.network_type === 'audio' && (
                <div className="grid grid-cols-2 gap-4 text-xs">
                  {subnetConfig.signal_type && (
                    <div>
                      <span className="block text-[10px] uppercase text-gray-400 font-bold">Тип сигнала:</span>
                      <span className="font-mono text-gray-800 font-bold">{subnetConfig.signal_type}</span>
                    </div>
                  )}
                  {subnetConfig.connector_type && (
                    <div>
                      <span className="block text-[10px] uppercase text-gray-400 font-bold">Тип разъема:</span>
                      <span className="font-mono text-gray-800 font-bold uppercase">{subnetConfig.connector_type}</span>
                    </div>
                  )}
                </div>
              )}

              {!isLan && !isModbus && currentNetwork.network_type !== 'audio' && subnetConfig.custom_params && (
                <div className="text-xs">
                  <span className="block text-[10px] uppercase text-gray-400 font-bold">Дополнительные параметры:</span>
                  <p className="font-mono text-gray-800 font-bold whitespace-pre-wrap mt-1">
                    {subnetConfig.custom_params}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Таблица */}
        {combinedRows.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-300 border border-gray-200 print-table text-sm">
              <thead className="bg-slate-50">
                <tr>
                  {renderHeader('Маркировка', 'deviceLabel')}
                  {renderHeader('Устройство / Клиент', 'deviceName')}
                  {renderHeader('Порт', 'portLabel', 'w-28')}
                  {isLan && (
                    <>
                      {renderHeader('IP-адрес', 'addressVal', 'font-mono w-44')}
                      {renderHeader('MAC-адрес', 'macAddress', 'font-mono w-48')}
                    </>
                  )}
                  {isModbus && (
                    <>
                      {renderHeader('Slave ID', 'addressVal', 'font-mono w-28')}
                    </>
                  )}
                  {renderHeader('Примечание', 'note')}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {combinedRows.map((row) => {
                  const isDuplicate = row.addressVal !== null && (addressOwners.get(row.addressVal)?.size || 0) > 1
                  return (
                    <tr 
                      key={row.id} 
                      className={`transition-colors print-no-break ${
                        isDuplicate 
                          ? 'bg-red-50/80 hover:bg-red-100/90 print:bg-red-50/40 text-red-950 print:text-black font-semibold' 
                          : 'hover:bg-slate-50/60 print:hover:bg-transparent'
                      }`}
                      title={isDuplicate ? 'Дубликат' : undefined}
                    >
                      {/* Маркировка */}
                      <td className="px-4 py-3 font-bold text-gray-950 whitespace-nowrap">
                        {row.deviceLabel || <span className="text-gray-400 italic">—</span>}
                      </td>

                      {/* Устройство */}
                      <td className="px-4 py-3 text-gray-900 font-medium">
                        {row.deviceName}
                      </td>

                      {/* Порт */}
                      <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                        {row.portLabel}
                      </td>

                      {/* IP адрес */}
                      {isLan && (
                        <>
                          <td className="px-4 py-3 font-mono text-gray-900">
                            <div className="flex items-center gap-1.5">
                              {row.fullIp ? (
                                <span className="font-bold">{row.fullIp}</span>
                              ) : (
                                <span className="text-gray-400 italic text-xs">не назначен</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 font-mono text-gray-600 text-xs">
                            {row.macAddress || <span className="text-gray-400 italic">—</span>}
                          </td>
                        </>
                      )}

                      {/* Slave ID */}
                      {isModbus && (
                        <td className="px-4 py-3 font-mono text-gray-900 font-bold">
                          <div className="flex items-center gap-1.5">
                            {row.addressVal !== null ? (
                              <span>{row.addressVal}</span>
                            ) : (
                              <span className="text-gray-400 italic text-xs">не назначен</span>
                            )}
                          </div>
                        </td>
                      )}

                      {/* Примечание */}
                      <td className="px-4 py-3 text-xs text-gray-600 markdown-content">
                        {row.note ? (
                          <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                            {row.note}
                          </ReactMarkdown>
                        ) : (
                          <span className="text-gray-300 italic">—</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-20 text-gray-400 border-2 border-dashed border-gray-200 rounded-2xl">
            <div className="text-5xl mb-3">🔌</div>
            <p className="text-base font-semibold text-gray-500">В этой сети нет привязанных устройств</p>
            <p className="text-xs text-gray-400 mt-1 max-w-sm mx-auto">
              Назначьте порты устройств к этой сети на странице управления интерфейсами, чтобы они появились в таблице.
            </p>
          </div>
        )}

        {/* Подпись / Дата печати */}
        <div className="mt-12 pt-6 border-t border-gray-100 flex justify-between text-[10px] text-gray-400 font-mono">
          <span>Сформировано автоматически в e2.bagsus.ru</span>
          <span>Дата выгрузки: {new Date().toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
        </div>

      </div>
    </div>
  )
}
